import Grid from '@mui/material/Grid';

import BlogArticle3Up from '@/components/BlogComponents/BlogArticle3Up/BlogArticle3Up';
import BlogHero from '@/components/BlogComponents/BlogHero/BlogHero';
import BlogInfo from '@/components/BlogComponents/BlogInfo/BlogInfo';
import BlogRichText from '@/components/BlogComponents/BlogRichText/BlogRichText';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import ArticleSchema from '@/components/Schema/ArticleSchema/ArticleSchema';
import { Section } from '@/components/Section/Section';
import SkipToContent from '@/components/SkipToContent/SkipToContent';
import type { BlogPageContent, Seo } from '@/contentful/contentful-exports';
import FooterComponent from '../Layout/Footer/Footer';
import HeaderComponent from '../Layout/Header/Header';
import FAQPageSchema from '../Schema/FAQPageSchema/FAQPageSchema';
import useStyles from './BlogPage.styles';

interface Props {
  path: string;
  seoFields: Seo;
  content: BlogPageContent;
}

export default function Blog(content: Props) {
  const { classes } = useStyles();
  const { seoFields, path } = content;
  const { fields, sys } = content.content;
  const {
    header,
    footer,
    sections,
    blogTitle,
    blogSubtitle,
    heroImage,
    heroImageAltText,
    video,
    author,
    authorBio,
    publicationDate,
    lastUpdated,
    body,
    socialShare,
    showTableOfContents,
    backButton,
    curatedArticlesListHeading,
    linkedArticleContent,
  } = fields;

  // Determine which date to show and label
  const displayDate = lastUpdated ? lastUpdated : publicationDate;
  const dateLabel = lastUpdated ? 'Last Updated' : 'Published';

  return (
    <>
      <ArticleSchema
        path={path}
        headline={blogTitle}
        description={seoFields.fields.metaText || ''}
        image={heroImage?.[0]}
        datePublished={publicationDate || new Date(sys.createdAt)}
        dateModified={new Date(sys.updatedAt)}
        lastUpdated={lastUpdated}
      />
      {sections && <FAQPageSchema sections={sections} />}
      <SkipToContent href='#blog' />
      <HeaderComponent header={header} />
      {/** biome-ignore lint/correctness/useUniqueElementIds: Automated migration to Biome */}
      <div className={classes.blog} id='blog'>
        <GridContainer fixed>
          <Grid className={classes.gridItem}>
            <BlogHero
              title={blogTitle}
              subtitle={blogSubtitle}
              image={heroImage}
              imageAltText={heroImageAltText}
              backButton={backButton}
              video={video}
            />
            <BlogInfo
              title={blogTitle}
              author={author}
              authorName={authorBio?.fields.name}
              authorImage={authorBio?.fields.image}
              authorImageAltText={authorBio?.fields.imageAltText}
              authorBio={authorBio?.fields.bio}
              authorLink={authorBio?.fields.link}
              publicationDate={displayDate}
              dateLabel={dateLabel}
              socialShare={socialShare}
              blogBody={body}
              showTableOfContents={showTableOfContents}
            />
          </Grid>
        </GridContainer>
        <div className={classes.blogRichTextWrapper}>
          <BlogRichText text={body} />
        </div>

        {/** biome-ignore lint/complexity/useOptionalChain: Automated migration to Biome */}
        {linkedArticleContent &&
          linkedArticleContent.curatedArticlesListCollection && (
            <BlogArticle3Up
              isCurated
              heading={curatedArticlesListHeading}
              initialArticles={
                linkedArticleContent.curatedArticlesListCollection?.items
              }
            />
          )}

        {sections?.map((section, index) => (
          <Section
            key={section.sys.id}
            section={section}
            sectionPosition={index}
          />
        ))}
      </div>
      <FooterComponent footer={footer} />
    </>
  );
}
